/*
 * Page for importing customers.  Initially this is specific to the FreshBooks integration.
 */
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import { Checkbox, Grid, Paper, Table, TableHead, TableCell, TableBody, TableRow, Typography } from '@mui/material';
import SaveButton from '../ui/SaveButton';

import CustomerFetchCountAndMaps from '../customer/CustomerFetchCountAndMaps';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import SearchField from '../ui/SearchField';
import AppBindStyles from '../../AppBindStyles';

const abEnvironment = new AbEnvironment();

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    maxWidth: 1200,
    marginTop: '16px'
  },
  radio: {
    marginTop: 16
  },
  tblCellCheckbox: {
    width: 40
  },
});

class CustomerImport extends Component {
  state = {
    customersWithMaps: null, // Customers that already have mappings
    customers: null,         // All customers.  Inefficient, but need this to exclude email matches (and potentially other matches)
    candidates: null,
    customerName: '',
    header_cb: false,
    cbs: [],

    // Save statuses for limits
    importSnackbarOpen: false,
    importSnackbarDuration: null,
    importStatus: null,
    importMessage: null
  };

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) { window.analytics.page(); }

    // Call the lookup
    let customersWithMaps = null;
    let customers = null;
    let accountId = abEnvironment.getUserAttribute('a_id');

    CustomerFetchCountAndMaps.fetch(accountId).toPromise()
    .then(cfcwm => {
      customersWithMaps = cfcwm.listmappedaccounts.integrations;
      customers = cfcwm.listmappedaccounts.customers;
      this.setState({customersWithMaps: customersWithMaps, customers: customers});

      // Call the external lookup with an empty parameter.
      this.externalCustomerLookup(null);
    })
    .catch(err => {
      //nothing to do....user will just need to search.
      this.setState({customersWithMaps: []});
    })
  }

  // Lookup integration customers based on a search pattern.
  externalCustomerLookup = (searchParm) => {
    const { callPartnerApi } = this.props.auth;
    let candidates = [];
    let cb_arr = [];

    //
    // If there is an integration active that allows for customer sync, we
    // can allow a reseller to choose a customer from an integrated system.
    //
    let integratedApps = abEnvironment.getUserAttribute('a_integrations');
    if (integratedApps == null || ! integratedApps.includes('FreshBooks')) {
      // Nothing to do
      return;
    }

    // Lookup in FreshBooks
    let payload = {
      operation: "FreshBooksListClients",
      params: {
        email: null,
        companyName: searchParm
      }
    };

    return callPartnerApi('appbind/generic',
                          'POST',
                          payload)
    .then(response => {
      if (response != null) {
        return response.json()
      } else {
        return null;
      }
    }).then(response => {
      if (response != null && response.body != null && response.body.body != null && response.body.body.response != null) {
        let theClients = response.body.body.response.result.clients;

        if (theClients.length > 0) {
          // The raw list needs some processing
          // - omit entries that are already mapped.  To do this, we'll need
          //   to check against customers already listed
          // - sort the list alphabetically
          let customersWithMaps = this.state.customersWithMaps;
          let customers = this.state.customers;

          for (let k=0; k<theClients.length; k++) {
            let clientNode = {
              customerStatus: 1,
              fbClientId: theClients[k].id,
              accountName: theClients[k].organization,
              contactFirstName: theClients[k].fname,
              contactLastName: theClients[k].lname,
              contactEmail: theClients[k].email
            };

            let pushNode = true;
            if (customersWithMaps != null && customersWithMaps.length > 0) {
              // If an integration is active with maps, check if this client is
              // already used in a mapping
              for (let i=0; i<customersWithMaps.length; i++) {
                let integration = customersWithMaps[i];
                if (integration.appName === "FreshBooks") {
                  let maps = integration.maps;
                  if (maps != null && maps.length > 0) {
                    for (let j=0; j<maps.length; j++) {
                      let map = maps[j];
                      let eIdNum = Number(map.entityId);
                      if (eIdNum === theClients[k].id) {
                        // skip this node
                        pushNode = false;
                        break;
                      }
                    }
                  }
                }
                if (pushNode === false) {
                  break;
                }
              }
            }

            // Also check all customers for a match on email
            if (customers != null && customers.length > 0) {
              for (let i=0; i<customers.length; i++) {
                let aCust = customers[i];
                if (aCust.accountEmail === clientNode.contactEmail) {
                  // skup this node
                  pushNode = false;
                  break;
                }
              }
            }

            if (pushNode) {
              candidates.push(clientNode);
              cb_arr.push(false);
            }
          }
        }
      }

      if (candidates.length > 0) {
        // Sort the candidates
        candidates.sort (function(a,b) {
          let aStr = '' + a.accountName;
          let bStr = '' + b.accountName;
          // alert('compare ' + aStr + ' to ' + bStr);
          return aStr.localeCompare(bStr);
        });
      }

      this.setState({candidates: candidates, header_cb: false, cbs: cb_arr});
    })
    .catch(err => {
      this.setState({candidates: []});
    })
  }

  isCandidateNotSelected = () => {
    if (this.state.cbs == null || this.state.cbs.length === 0) {
      return true;
    }

    for (let i=0; i<this.state.cbs.length; i++) {
      if (this.state.cbs[i] === true) {
        return false;
      }
    }

    return true;
  }

  handleChange = name => event => {
    let theValue = event.target.value;
    this.setState({
      [name]: theValue
    });

    if (name === "customerName") {
      if (theValue != null && theValue !== "") {
        this.externalCustomerLookup(theValue);
      } else {
        this.externalCustomerLookup(null);
      }
    }
  };

  handleCheckChange = (name, index) => event => {
    if (index == null) {
      // This is the header.  Set all to the appropriate value
      let cb_arr = [];
      for (let i=0;i<this.state.candidates.length; i++) {
        cb_arr.push(event.target.checked);
      }
      this.setState({[name]: event.target.checked, cbs: cb_arr});
    } else {
      // This is an individual checkbox
      let cb_arr = this.state.cbs;
      cb_arr[index] = event.target.checked;
      this.setState({cbs: cb_arr});
    }
  };

  importCustomers = () => {
    const { callPartnerApi } = this.props.auth;

    this.setState({importStatus: 'saving'});

    // For all of the customers that are checked, run the link
    // Need to check a few things:
    // - error conditions - what should happen when creation of a customer account in AppBind fails?
    //   - duplicate email is probably the main cause of that problem
    // - sequentially (call API sequentially), or new API that takes an array?
    //   - a new API is cleaner.  So send in an array of FB clients to import, and bring back an array of results.

    let cbs = this.state.cbs;
    let candidates = this.state.candidates;
    if (cbs == null || cbs.length <= 0 || candidates == null || candidates.length <= 0 || cbs.length !== candidates.length) {
      this.setState({ importSnackbarOpen: true, importStatus: 'error', importMessage: 'We were unable to import customers', importSnackbarDuration: null });
      return;
    }

    // Setup the client id array for clients to be imported.
    let fbClientIds = [];
    for (let i=0; i<cbs.length; i++) {
      if (cbs[i] === true) {
        fbClientIds.push(candidates[i].fbClientId);
      }
    }

    let payload = {
      operation: "ImportCustomersFromFreshBooks",
      params: {
        fbClientIds: fbClientIds
      }
    };

    return callPartnerApi('appbind/generic',
                          'POST',
                          payload)
    .then(response => {
      return response.json()
    }).then(response => {
      // TODO - when the operation API is standardized, we'll look at response.data.
      let importResponse = response;

      let successCount = importResponse.successCount;
      let errorCount = importResponse.errorCount;
      let totalCount = successCount + errorCount;

      let theStatus = 'success';
      let theMessage = '';
      let theDuration = 3000;

      if (totalCount <= 0) {
        theStatus = 'error';
        theDuration = null;
        theMessage = 'No customers were imported'
      } else if (successCount > 0 && errorCount > 0) {
        theStatus = 'error';
        theDuration = null;
        let sMsg = '' + successCount + ' customers were imported successfully';
        if (successCount === 1) {
          sMsg = 'One customer was imported successfully';
        }

        let eMsg = '' + errorCount + ' customers were not imported successfully';
        if (errorCount === 1) {
          eMsg = 'one customer was not imported successfully';
        }

        theMessage = sMsg + ', and ' + eMsg; 
      } else if (successCount > 0) {
        if (successCount === 1) {
          theMessage = 'The selected customer was imported successfully';
        } else {
          theMessage = 'All ' + successCount + ' selected customers were imported successfully';
        }
      } else if (errorCount > 0) {
        theStatus = 'error';
        theDuration = null;
        if (errorCount === 1) {
          theMessage = 'The selected customer was not imported successfully';
        } else {
          theMessage = 'All ' + errorCount + ' selected customers were not imported successfully';
        }
      } else {
        // Should not happen.
        theStatus = 'error';
        theDuration = null;
        theMessage = 'No customers were imported'
      }

      this.setState({ importSnackbarOpen: true, importStatus: theStatus, importMessage: theMessage, importSnackbarDuration: theDuration });
      return;
    })
    .catch(err => {
      this.setState({ importSnackbarOpen: true, importStatus: 'error', importMessage: 'No customers were imported successfully', importSnackbarDuration: null });
      return;
    })
  }

  render() {
    const { isAuthenticated, isAuthenticatedReseller, needAssent } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) {

      return ( <Redirect to='/home' /> );

    } else if (isAuthenticatedReseller()) {

      // Check if there is a FreshBooks integration active.
      let freshBooksActive = false;
      let integratedApps = abEnvironment.getUserAttribute('a_integrations');
      if (integratedApps != null && integratedApps.includes('FreshBooks')) {
        freshBooksActive = true;
      }

      if (freshBooksActive !== true) {
        return ( <Redirect to='/home' /> );
      }

      if (this.state.candidates == null) {
        // Return a loading spinny
        return (
          <div>
            <Typography>Loading...</Typography>
            <LoadingSpinner/>
          </div>
        );
      }

      let i=-1;

      return (
        <main>
          <PageTitle title="Customer import" subtitle="Select customers to import from FreshBooks"/>
          <Grid container spacing={1} direction="row" justifyContent="flex-start" style={{marginTop: '-24px'}}>
            <Grid item xs={12}>
              <SaveButton
                disabled={this.isCandidateNotSelected()}
                buttonType="add"
                iconMode="both"
                label="Import"
                snackbarOpen={this.state.importSnackbarOpen}
                autoHideDuration={this.state.importSnackbarDuration}
                onSaveClick={() => { this.importCustomers(); }}
                onSnackbarClose={() => { this._onSnackbarClose(); }}
                status={this.state.importStatus}
                message={this.state.importMessage}
              />
              {/* 
              <Button
                color="primary"
                variant="contained"
                disabled={this.isCandidateNotSelected()}
                style={AppBindStyles.addButtonOnSearch}
                onClick={() => {this.goTo('customercreate');}}
              >
                <Add/>Import
              </Button>
              */}
            </Grid>

            <Grid item>
              <SearchField
                id="customer-name-search"
                label="Search by name"
                value={this.state.customerName}
                onChange={this.handleChange('customerName')}
              />
            </Grid>
          </Grid>

          <Paper className={classes.paper}>
            <Table>
              <TableHead style={AppBindStyles.tableHead}>
                <TableRow style={AppBindStyles.tableHeadRow}>
                  <TableCell className={classes.tblCellCheckbox}><Checkbox checked={this.state.header_cb} onChange={this.handleCheckChange('header_cb', null)}/></TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
                <TableBody>
                  {this.state.candidates.map(n => {
                    i++;
                    return (
                      <TableRow key={n.fbClientId}>
                        <TableCell className={classes.tblCellCheckbox}><Checkbox checked={this.state.cbs[i]} onChange={this.handleCheckChange('header_cb', i)}/></TableCell>
                        <TableCell align="left">{n.accountName}</TableCell>
                        <TableCell align="left">{n.contactFirstName}&nbsp;{n.contactLastName}</TableCell>
                        <TableCell align="left">{n.contactEmail}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
            </Table>
          </Paper>
        </main>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }

  _onSnackbarClose() {
    this.setState( {importSnackbarOpen: false, importStatus: '', importMessage: '', importSnackbarDuration: null });
    window.location.reload();
  }

}

CustomerImport.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(CustomerImport);
